import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import axios from '../services/axios';

const RESUMEN_VACIO = {
  totalAdeudado: 0,
  cantidadBoletas: 0,
  cantidadClientes: 0,
};

function BoletasAdeudadasPage() {
  const navigate = useNavigate();
  const [datos, setDatos] = useState({
    resumen: RESUMEN_VACIO,
    zonaSeleccionada: null,
    periodos: [],
  });
  const [zonas, setZonas] = useState([]);
  const [zonaSeleccionada, setZonaSeleccionada] = useState('todas');
  const [periodosExpandidos, setPeriodosExpandidos] = useState(new Set());
  const [cargando, setCargando] = useState(true);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const zonaActualLabel = useMemo(() => {
    if (zonaSeleccionada === 'todas') {
      return 'Todas las zonas';
    }

    const zona = zonas.find((item) => item.id === Number(zonaSeleccionada));
    return zona?.nombre || datos.zonaSeleccionada?.nombre || 'Zona seleccionada';
  }, [datos.zonaSeleccionada?.nombre, zonaSeleccionada, zonas]);

  const showModal = (type, title, message) => {
    setModalConfig({
      isOpen: true,
      type,
      title,
      message,
    });
  };

  const cerrarModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const formatearMoneda = (valor) => new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(Number(valor || 0));

  const nombreMes = (mes) => {
    const meses = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
    ];

    return meses[mes - 1] || `Mes ${mes}`;
  };

  const togglePeriodo = (key) => {
    setPeriodosExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    const cargarZonas = async () => {
      try {
        const response = await axios.get('/zonas');
        setZonas(response.data || []);
      } catch (error) {
        console.error('Error al cargar zonas:', error);
        showModal('error', 'Error', 'No se pudieron cargar las zonas.');
      }
    };

    cargarZonas();
  }, []);

  useEffect(() => {
    const cargarReporte = async () => {
      setCargando(true);
      try {
        const params = zonaSeleccionada === 'todas'
          ? undefined
          : { zonaId: Number(zonaSeleccionada) };

        const response = await axios.get('/tarifario/boletas-adeudadas', { params });
        const periodos = response.data?.periodos || [];

        setDatos({
          resumen: response.data?.resumen || RESUMEN_VACIO,
          zonaSeleccionada: response.data?.zonaSeleccionada || null,
          periodos,
        });

        setPeriodosExpandidos(() => {
          if (periodos.length === 0) {
            return new Set();
          }

          const primerPeriodo = periodos[0];
          return new Set([`${primerPeriodo.anio}-${primerPeriodo.mes}`]);
        });
      } catch (error) {
        console.error('Error al cargar boletas adeudadas:', error);
        setDatos({
          resumen: RESUMEN_VACIO,
          zonaSeleccionada: null,
          periodos: [],
        });
        setPeriodosExpandidos(new Set());
        showModal(
          'error',
          'Error',
          error.response?.data?.message || 'No se pudo cargar el reporte de boletas adeudadas.',
        );
      } finally {
        setCargando(false);
      }
    };

    cargarReporte();
  }, [zonaSeleccionada]);

  const obtenerMensajeErrorPdf = async (error) => {
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        if (parsed?.message) {
          return parsed.message;
        }
      } catch (blobError) {
        return 'No se pudo generar el PDF del reporte.';
      }
    }

    return error.response?.data?.message || 'No se pudo generar el PDF del reporte.';
  };

  const descargarPdf = async () => {
    if (zonaSeleccionada === 'todas') {
      showModal(
        'warning',
        'Zona requerida',
        'Seleccione una zona para descargar el PDF del reporte.',
      );
      return;
    }

    if (datos.periodos.length === 0) {
      showModal(
        'warning',
        'Sin deuda',
        'No hay boletas adeudadas para la zona seleccionada.',
      );
      return;
    }

    setDescargandoPdf(true);
    try {
      const response = await axios.get('/tarifario/pdf/deudores-zona', {
        params: { zonaId: Number(zonaSeleccionada) },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const zonaSlug = zonaActualLabel.toLowerCase().replace(/\s+/g, '-');
      const fecha = new Date().toISOString().slice(0, 10);

      link.href = url;
      link.setAttribute('download', `boletas-adeudadas-${zonaSlug}-${fecha}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showModal(
        'success',
        'PDF generado',
        `Se descargó el reporte de boletas adeudadas para ${zonaActualLabel}.`,
      );
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      showModal(
        'error',
        'Error al generar PDF',
        await obtenerMensajeErrorPdf(error),
      );
    } finally {
      setDescargandoPdf(false);
    }
  };

  if (cargando) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center bg-[#f8fafb] dark:bg-background-dark">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Cargando reporte por mes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col bg-[#f8fafb] dark:bg-background-dark overflow-hidden">
        <div className="flex items-center gap-4 px-6 py-4 border-b bg-white dark:bg-gray-800 dark:border-gray-700">
          <button
            onClick={() => navigate('/administrativo')}
            className="flex items-center justify-center size-10 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <span className="material-symbols-outlined text-gray-700 dark:text-gray-300">arrow_back</span>
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Boletas Adeudadas
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Reporte por mes con deudores consolidados por período y zona
            </p>
          </div>
        </div>

        <div className="flex-1 p-6 overflow-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Adeudado</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatearMoneda(datos.resumen.totalAdeudado)}
                  </p>
                </div>
                <div className="size-12 rounded-lg bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                  <span className="material-symbols-outlined text-red-600 dark:text-red-400">
                    payments
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Boletas Adeudadas</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {datos.resumen.cantidadBoletas}
                  </p>
                </div>
                <div className="size-12 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                  <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">
                    receipt_long
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Clientes con Deuda</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {datos.resumen.cantidadClientes}
                  </p>
                </div>
                <div className="size-12 rounded-lg bg-yellow-100 dark:bg-yellow-900/20 flex items-center justify-center">
                  <span className="material-symbols-outlined text-yellow-600 dark:text-yellow-400">
                    people
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm mb-6 p-6">
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <div className="w-full lg:max-w-xs">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Filtrar por zona
                </label>
                <select
                  value={zonaSeleccionada}
                  onChange={(e) => setZonaSeleccionada(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="todas">Todas las zonas</option>
                  {zonas.map((zona) => (
                    <option key={zona.id} value={zona.id}>
                      {zona.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Zona actual
                </p>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {zonaActualLabel}
                </p>
              </div>

              <button
                onClick={descargarPdf}
                disabled={descargandoPdf || zonaSeleccionada === 'todas' || datos.periodos.length === 0}
                className="h-11 px-5 rounded-lg bg-primary text-white font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <span className={`material-symbols-outlined ${descargandoPdf ? 'animate-spin' : ''}`}>
                  {descargandoPdf ? 'progress_activity' : 'picture_as_pdf'}
                </span>
                {descargandoPdf ? 'Generando PDF...' : 'Descargar PDF'}
              </button>
            </div>
          </div>

          {datos.periodos.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-10 text-center">
              <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-4">
                receipt_long
              </span>
              <p className="text-gray-600 dark:text-gray-300 font-medium mb-1">
                No hay boletas adeudadas para {zonaActualLabel.toLowerCase()}.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Ajuste el filtro o intente nuevamente más tarde.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {datos.periodos.map((periodo) => {
                const key = `${periodo.anio}-${periodo.mes}`;
                const expanded = periodosExpandidos.has(key);

                return (
                  <div
                    key={key}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden"
                  >
                    <button
                      onClick={() => togglePeriodo(key)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                    >
                      <div className="flex items-center gap-4 text-left">
                        <span className="material-symbols-outlined text-gray-400">
                          {expanded ? 'expand_less' : 'expand_more'}
                        </span>
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {nombreMes(periodo.mes)} {periodo.anio}
                          </h2>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {periodo.resumen.cantidadClientes} deudor(es) | {periodo.resumen.cantidadBoletas} boleta(s)
                          </p>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-xl font-bold text-red-600">
                          {formatearMoneda(periodo.resumen.totalAdeudado)}
                        </p>
                      </div>
                    </button>

                    {expanded && (
                      <div className="border-t border-gray-200 dark:border-gray-700">
                        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30">
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            Deudores del período {nombreMes(periodo.mes)} {periodo.anio} en {zonaActualLabel.toLowerCase()}.
                          </p>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[920px]">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Padrón
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Cliente
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Zona
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Dirección
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Boletas
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Total Adeudado
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {periodo.deudores.map((deudor) => (
                                <tr key={`${key}-${deudor.cliente.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {deudor.cliente.padron || '-'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                      {deudor.cliente.nombre}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                      {deudor.zona?.nombre || 'Sin zona'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                      {deudor.cliente.direccion || '-'}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-right whitespace-nowrap">
                                    <span className="inline-flex items-center justify-center min-w-10 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                      {deudor.cantidadBoletas}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-right whitespace-nowrap">
                                    <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                                      {formatearMoneda(deudor.totalAdeudado)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={cerrarModal}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
      />
    </div>
  );
}

export default BoletasAdeudadasPage;
